/**
 * ToolStudioFlowPage
 * Step 0: Type selector (blurred backdrop, 2 big cards).
 * Steps 1-4 for webhook / Steps 1-3 for MCP: Guided creation flow (type-aware).
 */

import * as React from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { SecretSelectField } from '@/components/tools/SecretSelectField'
import { WizardSidebarStep } from '@/components/wizard/WizardSidebarStep'
import { getWizardStepStatus } from '@/components/wizard/step-status'
import {
  buildWebhookSchemasFromStudioDrafts,
  createHeaderDraft,
  createParamDraft,
  ensurePathPlaceholderInUrl,
  getWebhookHeaderValidationError,
  getWebhookParamValidationError,
  methodUsesQueryParams,
  mapKnownWebhookBackendValidationError,
  normalizeHeaderDrafts,
  normalizeWebhookMethod,
  parseStudioDraftsFromWebhookSchemas,
  reconcilePathParamsWithUrl,
  removePathPlaceholderFromUrl,
  renamePathPlaceholderInUrl,
  sanitizeEnumValues,
  toRequestHeadersPayload,
  type HeaderDraft,
  type ParamDraft,
} from '@/lib/tool-http-schema'
import { formatDrfValidationDetails } from '@/lib/format-drf-validation-details'
import { buildExternalMcpConfig } from '@/lib/tool-mcp-payload'
import { normalizeToolJsonPayload, validateToolJsonPayload, validateToolJsonText } from '@/lib/tool-json-validation'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { toolsService } from '@/services/toolsService'
import {
  clearToolStudioPersistedDraft,
  createInitialToolDraft,
  getStepTitlesForType,
  getTotalStepsForType,
  normalizeStudioVisibility,
  saveToolStudioPersistedDraft,
  useToolStudioPersistedState,
  type ToolDraft,
  type ToolType,
} from '@/hooks/studio/useToolStudioPersistence'
import type { MCPActionCreate, ToolVisibility } from '@/types/tool'
import { Separator } from '@/components/ui/separator'
import { SelectorDeEmoji } from '@/components/ui/selector-de-emoji'
import { TarjetaElegible } from '@/components/ui/tarjeta-elegible'
import { TagBadge } from '@/components/ui/tag-badge'

type DiscoveredMCPTool = {
  remote_tool_name: string
  display_name: string
  description?: string
  input_schema?: Record<string, unknown>
  enabled: boolean
}

/* ------------------------------------------------------------------ */
/*  Emoji helpers                                                      */
/* ------------------------------------------------------------------ */

const TOOL_EMOJI_STORAGE_KEY = 'tool-emojis'

function saveToolEmoji(toolId: string, emoji: string) {
  try {
    const map = JSON.parse(localStorage.getItem(TOOL_EMOJI_STORAGE_KEY) || '{}') as Record<string, string>
    if (emoji) map[toolId] = emoji
    else delete map[toolId]
    localStorage.setItem(TOOL_EMOJI_STORAGE_KEY, JSON.stringify(map))
  } catch { /* ignore */ }
}


/* ------------------------------------------------------------------ */
/*  SVG Logos                                                          */
/* ------------------------------------------------------------------ */

function McpLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="currentColor"
        d="M13.85 0a4.16 4.16 0 0 0-2.95 1.217L1.456 10.66a.835.835 0 0 0 0 1.18.835.835 0 0 0 1.18 0l9.442-9.442a2.49 2.49 0 0 1 3.541 0 2.49 2.49 0 0 1 0 3.541L8.59 12.97l-.1.1a.835.835 0 0 0 0 1.18.835.835 0 0 0 1.18 0l.1-.098 7.03-7.034a2.49 2.49 0 0 1 3.542 0l.049.05a2.49 2.49 0 0 1 0 3.54l-8.54 8.54a1.96 1.96 0 0 0 0 2.755l1.753 1.753a.835.835 0 0 0 1.18 0 .835.835 0 0 0 0-1.18l-1.753-1.753a.266.266 0 0 1 0-.394l8.54-8.54a4.185 4.185 0 0 0 0-5.9l-.05-.05a4.16 4.16 0 0 0-2.95-1.218c-.2 0-.401.02-.6.048a4.17 4.17 0 0 0-1.17-3.552A4.16 4.16 0 0 0 13.85 0m0 3.333a.84.84 0 0 0-.59.245L6.275 10.56a4.186 4.186 0 0 0 0 5.902 4.186 4.186 0 0 0 5.902 0L19.16 9.48a.835.835 0 0 0 0-1.18.835.835 0 0 0-1.18 0l-6.985 6.984a2.49 2.49 0 0 1-3.54 0 2.49 2.49 0 0 1 0-3.54l6.983-6.985a.835.835 0 0 0 0-1.18.84.84 0 0 0-.59-.245"
      />
    </svg>
  )
}

function WebhookLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  Type metadata                                                      */
/* ------------------------------------------------------------------ */

const TYPE_INFO: Record<ToolType, {
  label: string
  description: string
  Logo: React.FC<{ className?: string }>
}> = {
  webhook: {
    label: 'Webhook / HTTP Request',
    description: 'Petición HTTP a una URL externa',
    Logo: WebhookLogo,
  },
  mcp_server: {
    label: 'MCP Server',
    description: 'Servidor Model Context Protocol',
    Logo: McpLogo,
  },
}

/* ------------------------------------------------------------------ */
/*  TypeSelector overlay                                               */
/* ------------------------------------------------------------------ */

// Elegir el tipo de herramienta. Era un modal casero: `fixed inset-0` con su
// velo, su boton de cerrar dibujado a mano y ni foco atrapado ni Escape. Lo que
// hay dentro son dos opciones que se eligen, que es justo `TarjetaElegible`.
function TypeSelector({ onSelect, onCancel }: { onSelect: (t: ToolType) => void; onCancel: () => void }) {
  const isMobile = useIsMobile()
  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) onCancel() }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Elige el tipo de herramienta</DialogTitle>
          <DialogDescription>Selecciona cómo se ejecutará esta herramienta</DialogDescription>
        </DialogHeader>
        <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
          {(['webhook', 'mcp_server'] as ToolType[]).map((type) => {
            const { label, description, Logo } = TYPE_INFO[type]
            return (
              <TarjetaElegible
                key={type}
                onElegir={() => onSelect(type)}
                icono={<Logo className={isMobile ? 'h-10 w-10' : 'h-16 w-16'} />}
                nombre={
                  <>
                    <span className={cn('block font-semibold', isMobile ? 'text-sm' : 'text-lg')}>{label}</span>
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">{description}</span>
                  </>
                }
              />
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ */
/*  Error helper                                                       */
/* ------------------------------------------------------------------ */

function hasNameCollisionInText(value: unknown): boolean {
  const text = String(value ?? '').toLowerCase()
  return (
    (text.includes('name') || text.includes('nombre')) &&
    (
      text.includes('already exists')
      || text.includes('ya existe')
      || text.includes('must make a unique')
      || text.includes('unique constraint')
      || text.includes('duplicate')
    )
  )
}

const NAME_COLLISION_ERROR = '__NAME_COLLISION__'

function toErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body
    if (body && typeof body === 'object') {
      const wrappedError = (body as { error?: unknown }).error
      if (wrappedError && typeof wrappedError === 'object') {
        const wrappedMessage = (wrappedError as { message?: unknown }).message
        const details = (wrappedError as { details?: unknown }).details
        const nameDetails =
          details && typeof details === 'object'
            ? (details as Record<string, unknown>).name
            : undefined
        if (nameDetails !== undefined && hasNameCollisionInText(nameDetails)) {
          return NAME_COLLISION_ERROR
        }
        if (typeof wrappedMessage === 'string' && wrappedMessage.trim().length > 0) {
          if (details && typeof details === 'object') {
            const detailEntries = Object.entries(details as Record<string, unknown>)
            if (detailEntries.some(([, value]) => hasNameCollisionInText(value))) {
              return NAME_COLLISION_ERROR
            }
            const detailStr = formatDrfValidationDetails(details)
            if (detailStr.trim().length > 0) {
              if (hasNameCollisionInText(detailStr)) {
                return NAME_COLLISION_ERROR
              }
              const knownDetailMessage = mapKnownWebhookBackendValidationError(detailStr)
              if (knownDetailMessage) return knownDetailMessage
              return `${wrappedMessage} (${detailStr})`
            }
          }
          return wrappedMessage
        }
      }
      const handshakeError = (body as { handshake?: { error?: unknown } }).handshake?.error
      if (typeof handshakeError === 'string' && handshakeError.trim().length > 0) {
        return handshakeError
      }
      const bodyError = (body as { error?: unknown }).error
      if (typeof bodyError === 'string' && bodyError.trim().length > 0) {
        if (hasNameCollisionInText(bodyError)) {
          return NAME_COLLISION_ERROR
        }
        const knownBodyError = mapKnownWebhookBackendValidationError(bodyError)
        if (knownBodyError) return knownBodyError
        return bodyError
      }
      const entries = Object.entries(body as Record<string, unknown>)
      if (entries.length > 0) {
        for (const [field, value] of entries) {
          if (hasNameCollisionInText(value)) {
            return NAME_COLLISION_ERROR
          }
          const detailStr = formatDrfValidationDetails(value)
          if (detailStr.trim().length > 0) {
            if (hasNameCollisionInText(detailStr)) {
              return NAME_COLLISION_ERROR
            }
            return `${field}: ${detailStr}`
          }
        }
      }
    }
  }
  if (error instanceof Error && error.message) return error.message
  return 'Ha ocurrido un error inesperado.'
}

function isValidHttpUrl(value: string): boolean {
  const candidate = value.trim()
  if (!candidate) return false
  try {
    const parsed = new URL(candidate)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/* ------------------------------------------------------------------ */
/*  StepLayout                                                         */
/* ------------------------------------------------------------------ */

function StepLayout({
  title,
  subtitle,
  children,
  footer,
  isMobile,
}: {
  title: string
  subtitle: string
  children: React.ReactNode
  footer: React.ReactNode
  isMobile?: boolean
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      {!isMobile && (
        <div className="border-b border-border px-6 py-5 md:px-8">
          <h2 className="text-2xl font-semibold tracking-tight md:text-2xl">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground md:text-base">{subtitle}</p>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="mx-auto w-full max-w-5xl p-6 md:p-8">{children}</div>
        </ScrollArea>
      </div>
      <div className="border-t border-border bg-muted/10 px-6 py-4 md:px-8">{footer}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

function slugifyToolName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// El confeti se pinta con `style`, no con clases, asi que los tokens viajan
// escritos como `hsl(var(--x))`. Eran seis hex sueltos que no cambiaban con el
// tema: en modo claro el amarillo sobre blanco no se veia.
const CONFETTI_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--success))',
  'hsl(var(--warning))',
  'hsl(var(--destructive))',
  'hsl(var(--primary) / 0.6)',
  'hsl(var(--success) / 0.6)',
]

function CelebrationConfetti({ seed }: { seed: number }) {
  const pieces = React.useMemo(() => {
    const seededRandom = (value: number): number => {
      const x = Math.sin((value + 1) * (seed + 1) * 12.9898) * 43758.5453
      return x - Math.floor(x)
    }

    return Array.from({ length: 80 }, (_, index) => ({
      id: index,
      left: seededRandom(index) * 100,
      xDrift: (seededRandom(index + 17) - 0.5) * 180,
      rotation: (seededRandom(index + 41) - 0.5) * 540,
      duration: 1.9 + seededRandom(index + 73) * 1.2,
      delay: seededRandom(index + 99) * 0.25,
      size: 6 + seededRandom(index + 131) * 7,
      color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    }))
  }, [seed])

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((piece) => (
        <motion.span
          key={`${seed}-${piece.id}`}
          className="absolute rounded-md"
          style={{
            left: `${piece.left}%`,
            top: -24,
            width: piece.size,
            height: piece.size * 0.55,
            backgroundColor: piece.color,
          }}
          initial={{ y: -20, x: 0, rotate: 0, opacity: 0 }}
          animate={{ y: 980, x: piece.xDrift, rotate: piece.rotation, opacity: [0, 1, 1, 0] }}
          transition={{ duration: piece.duration, delay: piece.delay, ease: 'easeOut' }}
        />
      ))}
    </div>
  )
}

export function ToolStudioFlowPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { lang } = useParams<{ lang: string }>()
  const langPrefix = `/${lang || 'en'}`
  const persistedState = useToolStudioPersistedState()

  const [toolType, setToolType] = React.useState<ToolType | null>(persistedState?.toolType ?? null)
  const [step, setStep] = React.useState(persistedState?.step ?? 1)
  const [creating, setCreating] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<ToolDraft>(() => persistedState?.draft ?? createInitialToolDraft())
  const [editorMode, setEditorMode] = React.useState<'form' | 'json'>('form')
  const [jsonDraft, setJsonDraft] = React.useState('')
  const [jsonError, setJsonError] = React.useState<string | null>(null)
  const [mcpDiscovering, setMcpDiscovering] = React.useState(false)
  const [mcpExploreError, setMcpExploreError] = React.useState<string | null>(null)
  const [mcpDiscoveredTools, setMcpDiscoveredTools] = React.useState<DiscoveredMCPTool[]>([])
  const [mcpToolDetail, setMcpToolDetail] = React.useState<DiscoveredMCPTool | null>(null)
  const [existingInternalNames, setExistingInternalNames] = React.useState<Set<string>>(new Set())
  const [loadingInternalNames, setLoadingInternalNames] = React.useState(false)
  const [internalNamesError, setInternalNamesError] = React.useState<string | null>(null)
  const [confirmResetOpen, setConfirmResetOpen] = React.useState(false)
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false)
  const [createdCount, setCreatedCount] = React.useState(0)
  const [confettiSeed, setConfettiSeed] = React.useState(0)
  const [paramEnumInputByKey, setParamEnumInputByKey] = React.useState<Record<string, string>>({})
  const [visitedSteps, setVisitedSteps] = React.useState<Set<number>>(
    () => new Set(persistedState?.visitedSteps?.length ? persistedState.visitedSteps : [1])
  )
  const [editingField, setEditingField] = React.useState<
    | 'display_name'
    | 'description'
    | 'timeout_seconds'
    | 'retry_count'
    | 'http_method'
    | 'http_url'
    | 'mcp_url'
    | 'classification'
    | null
  >(null)

  const totalSteps = React.useMemo(() => (toolType ? getTotalStepsForType(toolType) : getTotalStepsForType('webhook')), [toolType])
  const stepTitles = React.useMemo(() => (toolType ? getStepTitlesForType(toolType) : getStepTitlesForType('webhook')), [toolType])
  const reviewStep = toolType === 'mcp_server' ? 3 : 4
  const progress = step > totalSteps ? 100 : Math.round((step / totalSteps) * 100)
  const derivedToolName = React.useMemo(
    () => slugifyToolName(draft.display_name),
    [draft.display_name]
  )

  React.useEffect(() => {
    setStep((prev) => {
      if (prev < 1) return 1
      if (prev > totalSteps) return totalSteps
      return prev
    })
    setVisitedSteps((prev) => {
      const filtered = Array.from(prev).filter((value) => value >= 1 && value <= totalSteps)
      if (filtered.length === prev.size) return prev
      return new Set(filtered.length > 0 ? filtered : [1])
    })
  }, [totalSteps])

  React.useEffect(() => {
    if (!toolType || step > totalSteps) {
      clearToolStudioPersistedDraft()
      return
    }
    saveToolStudioPersistedDraft({
      toolType,
      step: Math.min(Math.max(step, 1), totalSteps),
      visitedSteps: Array.from(visitedSteps)
        .filter((value) => value >= 1 && value <= totalSteps)
        .sort((a, b) => a - b),
      draft,
    })
  }, [toolType, step, visitedSteps, draft, totalSteps])

  React.useEffect(() => {
    if (toolType !== 'mcp_server') {
      setMcpDiscoveredTools([])
      setMcpExploreError(null)
    }
  }, [toolType])

  React.useEffect(() => {
    if (!toolType) {
      setExistingInternalNames(new Set())
      setLoadingInternalNames(false)
      setInternalNamesError(null)
      return
    }
    let cancelled = false
    setLoadingInternalNames(true)
    toolsService
      .listActions()
      .then((actions) => {
        if (cancelled) return
        const names = new Set(
          actions
            .map((action) => String(action.name || '').trim().toLowerCase())
            .filter((name) => name.length > 0)
        )
        setExistingInternalNames(names)
        setInternalNamesError(null)
      })
      .catch(() => {
        if (cancelled) return
        setExistingInternalNames(new Set())
        setInternalNamesError('No se pudo validar si el nombre interno ya existe. Se validará al crear.')
      })
      .finally(() => {
        if (cancelled) return
        setLoadingInternalNames(false)
      })

    return () => {
      cancelled = true
    }
  }, [toolType])

  React.useEffect(() => {
    if (draft.name === derivedToolName) return
    setDraft((prev) => ({ ...prev, name: derivedToolName }))
  }, [draft.name, derivedToolName])

  const mapStudioError = React.useCallback((error: unknown) => {
    const message = toErrorMessage(error)
    if (message === NAME_COLLISION_ERROR) {
      return t('agent_studio.internal_name_collision')
    }
    return message
  }, [t])

  const paramInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({})
  const pendingParamFocusRef = React.useRef<{ refKey: string; caret: number | null } | null>(null)

  const buildParamStableKey = React.useCallback(
    (kind: 'path_params_schema' | 'query_params_schema', param: ParamDraft, paramIndex: number) => {
      const stableParamKey = param.draft_id || `${kind}-${paramIndex}`
      return `${kind}:${stableParamKey}`
    },
    []
  )

  const buildParamInputRefKey = React.useCallback(
    (
      kind: 'path_params_schema' | 'query_params_schema',
      param: ParamDraft,
      paramIndex: number,
      field: 'id' | 'description' | 'value'
    ) => `${buildParamStableKey(kind, param, paramIndex)}:${field}`,
    [buildParamStableKey]
  )

  const setParamInputNode = React.useCallback((refKey: string, node: HTMLInputElement | null) => {
    if (node) {
      paramInputRefs.current[refKey] = node
      return
    }
    delete paramInputRefs.current[refKey]
  }, [])

  const rememberParamInputFocus = React.useCallback((refKey: string, target: HTMLInputElement) => {
    pendingParamFocusRef.current = {
      refKey,
      caret: target.selectionStart,
    }
  }, [])

  React.useLayoutEffect(() => {
    const pending = pendingParamFocusRef.current
    if (!pending) return
    pendingParamFocusRef.current = null

    const target = paramInputRefs.current[pending.refKey]
    if (!target || document.activeElement === target) return

    target.focus({ preventScroll: true })
    if (pending.caret !== null) {
      const caret = Math.min(Math.max(pending.caret, 0), target.value.length)
      target.setSelectionRange(caret, caret)
    }
  }, [draft.http_config.path_params_schema, draft.http_config.query_params_schema])

  const updateDraft = <K extends keyof ToolDraft>(field: K, value: ToolDraft[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  const syncDraftUrlAndPathParams = React.useCallback((nextUrl: string) => {
    setDraft((prev) => {
      return {
        ...prev,
        http_config: {
          ...prev.http_config,
          url: nextUrl,
          path_params_schema: reconcilePathParamsWithUrl(nextUrl, prev.http_config.path_params_schema),
        },
      }
    })
  }, [])

  const goToStep = (target: number) => {
    if (!ensureJsonApplied()) return
    if (target < 1 || target > totalSteps) return
    if (target > step && !isStepValid(step)) {
      if (step === 1) {
        if (!draft.display_name.trim()) {
          setErrorMessage('El nombre visible es obligatorio.')
        } else if (!derivedToolName.length) {
          setErrorMessage('El nombre visible debe incluir letras o números para generar un nombre interno válido.')
        } else if (loadingInternalNames) {
          setErrorMessage('Validando disponibilidad del nombre interno...')
        } else if (internalNameTaken) {
          setErrorMessage(t('agent_studio.internal_name_collision'))
        } else if (!draft.description.trim()) {
          setErrorMessage('La descripción es obligatoria.')
        } else {
          setErrorMessage('Completa los campos requeridos antes de continuar.')
        }
      } else if (step === 2 && toolType === 'mcp_server') {
        setErrorMessage('Primero valida la conexión MCP y selecciona al menos una herramienta.')
      } else if (step === 2 && toolType === 'webhook') {
        if (!step2UrlValid) {
          setErrorMessage('La URL debe ser absoluta y comenzar con http:// o https://.')
        } else if (webhookHeadersError) {
          setErrorMessage(webhookHeadersError)
        } else {
          setErrorMessage('Completa los campos requeridos antes de continuar.')
        }
      } else if (step === 3 && toolType === 'webhook' && webhookSchemaError) {
        setErrorMessage(webhookSchemaError)
      } else {
        setErrorMessage('Completa los campos requeridos antes de continuar.')
      }
      return
    }
    setErrorMessage(null)
    setStep(target)
    setVisitedSteps((prev) => new Set(prev).add(target))
  }

  const closeStudio = React.useCallback(() => {
    if (toolType && step <= totalSteps) {
      setConfirmCloseOpen(true)
      return
    }
    navigate(`${langPrefix}/tools`)
  }, [langPrefix, navigate, step, toolType, totalSteps])

  const discardDraftAndClose = React.useCallback(() => {
    clearToolStudioPersistedDraft()
    setConfirmCloseOpen(false)
    navigate(`${langPrefix}/tools`)
  }, [langPrefix, navigate])

  const keepDraftAndClose = React.useCallback(() => {
    setConfirmCloseOpen(false)
    navigate(`${langPrefix}/tools`)
  }, [langPrefix, navigate])

  const resetStudioFromScratch = React.useCallback(() => {
    clearToolStudioPersistedDraft()
    setToolType(null)
    setStep(1)
    setVisitedSteps(new Set([1]))
    setDraft(createInitialToolDraft())
    setEditorMode('form')
    setJsonDraft('')
    setJsonError(null)
    setErrorMessage(null)
    setMcpDiscoveredTools([])
    setMcpExploreError(null)
    setMcpToolDetail(null)
    setEditingField(null)
    setConfirmResetOpen(false)
    setConfirmCloseOpen(false)
    setParamEnumInputByKey({})
  }, [])

  const handleTypeSelect = (type: ToolType) => {
    setToolType(type)
    setDraft(createInitialToolDraft())
    setStep(1)
    setVisitedSteps(new Set([1]))
    setEditorMode('form')
    setJsonDraft('')
    setJsonError(null)
    setParamEnumInputByKey({})
  }

  const handleTypeChangeAtStepOne = (type: ToolType) => {
    if (toolType === type) return
    setToolType(type)
    setErrorMessage(null)
  }

  const buildJsonSnapshot = React.useCallback(() => {
    if (!toolType) return ''
    if (toolType === 'mcp_server') {
      const enabledRemoteTools = mcpDiscoveredTools
        .filter((item) => item.enabled)
        .map((item) => item.remote_tool_name)
      const remoteCatalog = mcpDiscoveredTools.map((item) => ({
        remote_tool_name: item.remote_tool_name,
        display_name: item.display_name || item.remote_tool_name,
        description: item.description || '',
        input_schema: item.input_schema || {},
      }))

      return JSON.stringify(
        {
          execution_type: toolType,
          ...draft,
          mcp_enabled_remote_tools: enabledRemoteTools,
          http_config: {
            method: 'POST',
            url: draft.mcp_url,
            external_mcp: {
              server_url: draft.mcp_url,
              transport: draft.mcp_transport,
              auth_type: draft.mcp_auth_type,
              auth_header_name: draft.mcp_auth_header_name,
              auth_secret_id: draft.mcp_auth_secret_id,
              enabled_remote_tools: enabledRemoteTools,
              remote_tools_catalog: remoteCatalog,
            },
          },
        },
        null,
        2
      )
    }
    if (toolType === 'webhook') {
      const schemaPayload = buildWebhookSchemasFromStudioDrafts(
        draft.http_config.method,
        draft.http_config.path_params_schema,
        draft.http_config.query_params_schema
      )
      return JSON.stringify(
        {
          execution_type: toolType,
          ...draft,
          http_config: {
            ...draft.http_config,
            ...schemaPayload,
          },
        },
        null,
        2
      )
    }
    return JSON.stringify({ execution_type: toolType, ...draft }, null, 2)
  }, [toolType, draft, mcpDiscoveredTools])

  const applyJsonDraft = (): boolean => {
    const validationMessage = validateToolJsonText(jsonDraft)
    if (validationMessage) {
      setJsonError(validationMessage)
      return false
    }
    const parsed = JSON.parse(jsonDraft) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') {
      setJsonError('El contenido JSON debe ser un objeto.')
      return false
    }

    const validationErrors = validateToolJsonPayload(parsed)
    if (validationErrors.length > 0) {
      setJsonError(validationErrors.join('\n'))
      return false
    }

    const normalizedParsed = normalizeToolJsonPayload(parsed)
    const parsedExecutionType = normalizedParsed.execution_type
    const executionType: ToolType =
      parsedExecutionType === 'webhook' || parsedExecutionType === 'mcp_server'
        ? parsedExecutionType
        : toolType!

    const httpConfigRaw = normalizedParsed.http_config
    const httpConfig =
      httpConfigRaw && typeof httpConfigRaw === 'object'
        ? (httpConfigRaw as Record<string, unknown>)
        : null
    const externalMcpRaw = httpConfig?.external_mcp
    const externalMcp =
      externalMcpRaw && typeof externalMcpRaw === 'object'
        ? (externalMcpRaw as Record<string, unknown>)
        : null

    const normalizeHttpMethod = (value: unknown): ToolDraft['http_config']['method'] => {
      if (typeof value !== 'string') return 'GET'
      const upper = value.toUpperCase()
      if (upper === 'GET' || upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE') {
        return upper
      }
      return 'GET'
    }

    const parsedHttpConfig =
      httpConfig
        ? {
            method: normalizeHttpMethod(httpConfig.method),
            url: String(httpConfig.url || ''),
            request_headers: normalizeHeaderDrafts(httpConfig as ToolDraft['http_config']),
            ...parseStudioDraftsFromWebhookSchemas(
              normalizeWebhookMethod(httpConfig.method),
              (httpConfig as { path_params_schema?: unknown }).path_params_schema,
              (httpConfig as { query_params_schema?: unknown }).query_params_schema,
              (httpConfig as { request_body_schema?: unknown }).request_body_schema,
            ),
          }
        : draft.http_config

    const parsedMcpUrlRaw = (normalizedParsed as { mcp_url?: unknown }).mcp_url
    const parsedMcpUrl =
      typeof parsedMcpUrlRaw === 'string'
        ? parsedMcpUrlRaw
        : (typeof externalMcp?.server_url === 'string'
            ? externalMcp.server_url
            : (typeof parsedHttpConfig.url === 'string' ? parsedHttpConfig.url : draft.mcp_url))

    const parsedMcpTransportRaw = (normalizedParsed as { mcp_transport?: unknown }).mcp_transport ?? externalMcp?.transport
    const parsedMcpAuthTypeRaw = (normalizedParsed as { mcp_auth_type?: unknown }).mcp_auth_type ?? externalMcp?.auth_type
    const parsedMcpHeaderNameRaw = (normalizedParsed as { mcp_auth_header_name?: unknown }).mcp_auth_header_name ?? externalMcp?.auth_header_name
    const parsedMcpSecretIdRaw = (normalizedParsed as { mcp_auth_secret_id?: unknown }).mcp_auth_secret_id ?? externalMcp?.auth_secret_id

    const enabledToolsFromRoot = (normalizedParsed as { mcp_enabled_remote_tools?: unknown }).mcp_enabled_remote_tools
    const enabledToolsFromExternal = externalMcp?.enabled_remote_tools
    const enabledToolsProvided = Array.isArray(enabledToolsFromRoot) || Array.isArray(enabledToolsFromExternal)
    const normalizedEnabledTools = (
      Array.isArray(enabledToolsFromRoot)
        ? enabledToolsFromRoot
        : (Array.isArray(enabledToolsFromExternal) ? enabledToolsFromExternal : [])
    )
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean)

    const catalogFromExternalRaw = externalMcp?.remote_tools_catalog
    const catalogProvided = Array.isArray(catalogFromExternalRaw)
    const normalizedCatalog = Array.isArray(catalogFromExternalRaw)
      ? catalogFromExternalRaw
          .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
          .map((item) => ({
            remote_tool_name: String(item.remote_tool_name || '').trim(),
            display_name: String(item.display_name || item.remote_tool_name || '').trim(),
            description: typeof item.description === 'string' ? item.description : '',
            input_schema: item.input_schema && typeof item.input_schema === 'object'
              ? (item.input_schema as Record<string, unknown>)
              : {},
          }))
          .filter((item) => item.remote_tool_name.length > 0)
      : []

    if (executionType === 'mcp_server' && (enabledToolsProvided || catalogProvided)) {
      if (normalizedCatalog.length > 0) {
        const enabledSet = new Set(normalizedEnabledTools)
        setMcpDiscoveredTools(
          normalizedCatalog.map((item) => ({
            ...item,
            enabled: enabledToolsProvided ? enabledSet.has(item.remote_tool_name) : true,
          }))
        )
      } else {
        setMcpDiscoveredTools(
          normalizedEnabledTools.map((remoteToolName) => ({
            remote_tool_name: remoteToolName,
            display_name: remoteToolName,
            description: '',
            input_schema: {},
            enabled: true,
          }))
        )
      }
    }

    const nextDraft: ToolDraft = {
      name: typeof normalizedParsed.name === 'string' ? normalizedParsed.name : draft.name,
      display_name: typeof normalizedParsed.display_name === 'string' ? normalizedParsed.display_name : draft.display_name,
      description: typeof normalizedParsed.description === 'string' ? normalizedParsed.description : draft.description,
      is_enabled: normalizedParsed.is_enabled !== undefined ? Boolean(normalizedParsed.is_enabled) : draft.is_enabled,
      http_config: parsedHttpConfig,
      timeout_seconds: Number(normalizedParsed.timeout_seconds ?? draft.timeout_seconds) || draft.timeout_seconds,
      retry_count: Number(normalizedParsed.retry_count ?? draft.retry_count) || draft.retry_count,
      mcp_url: String(parsedMcpUrl || ''),
      mcp_transport: (
        parsedMcpTransportRaw === 'http_jsonrpc' || parsedMcpTransportRaw === 'streamable_http'
          ? parsedMcpTransportRaw
          : draft.mcp_transport
      ),
      mcp_auth_type: (
        parsedMcpAuthTypeRaw === 'none' || parsedMcpAuthTypeRaw === 'bearer' || parsedMcpAuthTypeRaw === 'header'
          ? parsedMcpAuthTypeRaw
          : draft.mcp_auth_type
      ),
      mcp_auth_header_name: typeof parsedMcpHeaderNameRaw === 'string' ? parsedMcpHeaderNameRaw : draft.mcp_auth_header_name,
      mcp_auth_secret_id: typeof parsedMcpSecretIdRaw === 'string' ? parsedMcpSecretIdRaw : draft.mcp_auth_secret_id,
      emoji: typeof normalizedParsed.emoji === 'string' ? normalizedParsed.emoji : draft.emoji,
      classification: normalizeStudioVisibility(normalizedParsed.classification as ToolVisibility | undefined),
    }

    setToolType(executionType)
    setDraft(nextDraft)
    setJsonError(null)
    return true
  }

  const ensureJsonApplied = () => {
    if (editorMode !== 'json') return true
    return applyJsonDraft()
  }

  /* Validation */
  const internalNameTaken = derivedToolName.length > 0 && existingInternalNames.has(derivedToolName.toLowerCase())
  const validateStep1 = draft.display_name.trim().length > 0
    && derivedToolName.length > 0
    && draft.description.trim().length > 0
    && !internalNameTaken
    && !loadingInternalNames
  const step2UrlValue = toolType === 'webhook' ? draft.http_config.url : draft.mcp_url
  const step2UrlValid = isValidHttpUrl(step2UrlValue)
  const webhookHeadersError = toolType === 'webhook'
    ? getWebhookHeaderValidationError(draft.http_config.request_headers)
    : null
  const webhookMethodUsesQuery = methodUsesQueryParams(draft.http_config.method)
  const webhookSchemaError = toolType === 'webhook'
    ? (
      getWebhookParamValidationError(draft.http_config.path_params_schema, 'ruta')
      ?? getWebhookParamValidationError(draft.http_config.query_params_schema, webhookMethodUsesQuery ? 'consulta' : 'cuerpo')
    )
    : null
  const selectedMcpToolsCount = mcpDiscoveredTools.filter((tool) => tool.enabled).length
  const validateStep2 = step2UrlValid
    && (
      toolType !== 'mcp_server' || draft.mcp_auth_type === 'none' || draft.mcp_auth_secret_id.trim().length > 0
    )
    && (
      toolType !== 'webhook' || webhookHeadersError === null
    )
    && (
      toolType !== 'mcp_server' || selectedMcpToolsCount > 0
    )
  const validateStep3 = toolType === 'mcp_server' || webhookSchemaError === null

  const isStepValid = React.useCallback(
    (itemStep: number) => {
      if (itemStep === 1) return validateStep1
      if (itemStep === 2) return validateStep2
      if (itemStep === 3) return validateStep3
      if (itemStep === 4) return validateStep1 && validateStep2 && validateStep3
      return false
    },
    [validateStep1, validateStep2, validateStep3]
  )

  const discoverMcpTools = async () => {
    if (!isValidHttpUrl(draft.mcp_url)) {
      setMcpExploreError('La URL debe ser absoluta y comenzar con http:// o https://.')
      return
    }
    if (draft.mcp_auth_type !== 'none' && !draft.mcp_auth_secret_id.trim()) {
      setMcpExploreError('La API key/token es obligatoria para este tipo de autenticación.')
      return
    }
    setMcpDiscovering(true)
    setMcpExploreError(null)
    try {
      const response = await toolsService.externalExplore({
        server_url: draft.mcp_url.trim(),
        transport: draft.mcp_transport,
        auth_type: draft.mcp_auth_type,
        auth_header_name: draft.mcp_auth_header_name || 'X-API-Key',
        auth_secret_id: draft.mcp_auth_type === 'none' ? '' : draft.mcp_auth_secret_id.trim(),
        timeout_seconds: draft.timeout_seconds,
      })
      const nextTools = (response.tools || []).map((tool) => {
        const remote = String(tool.remote_tool_name || '')
        const existing = mcpDiscoveredTools.find((item) => item.remote_tool_name === remote)
        return {
          remote_tool_name: remote,
          display_name: String(tool.display_name || remote),
          description: tool.description,
          input_schema: tool.input_schema,
          enabled: existing ? existing.enabled : true,
        }
      }).filter((tool) => tool.remote_tool_name.length > 0)
      setMcpDiscoveredTools(nextTools)
      if (nextTools.length === 0) {
        setMcpExploreError('El servidor respondió, pero no expone herramientas en tools/list.')
      }
    } catch (error) {
      setMcpExploreError(mapStudioError(error))
      setMcpDiscoveredTools([])
    } finally {
      setMcpDiscovering(false)
    }
  }

  const toggleMcpDiscoveredTool = (remoteToolName: string, enabled: boolean) => {
    setMcpDiscoveredTools((prev) =>
      prev.map((tool) => (
        tool.remote_tool_name === remoteToolName ? { ...tool, enabled } : tool
      ))
    )
  }

  /* Create tool */
  const createTool = async () => {
    if (!ensureJsonApplied()) return
    if (loadingInternalNames) {
      setErrorMessage('Validando disponibilidad del nombre interno...')
      return
    }
    if (internalNameTaken) {
      setErrorMessage(t('agent_studio.internal_name_collision'))
      return
    }
    if (!validateStep2) {
      if (toolType === 'mcp_server') {
        setErrorMessage('Revisa la URL MCP, autenticación y selecciona al menos una herramienta.')
      } else if (!step2UrlValid) {
        setErrorMessage('La URL debe ser absoluta y comenzar con http:// o https://.')
      } else if (webhookHeadersError) {
        setErrorMessage(webhookHeadersError)
      } else {
        setErrorMessage('Completa los campos requeridos antes de continuar.')
      }
      return
    }
    if (toolType === 'webhook' && webhookSchemaError) {
      setErrorMessage(webhookSchemaError)
      return
    }
    setCreating(true)
    setErrorMessage(null)

    try {
      if (toolType === 'mcp_server') {
        const selectedTools = mcpDiscoveredTools.filter((tool) => tool.enabled)
        const payload: MCPActionCreate = {
          name: derivedToolName,
          display_name: draft.display_name.trim(),
          description: draft.description.trim(),
          execution_type: 'mcp_server',
          is_enabled: draft.is_enabled,
          timeout_seconds: draft.timeout_seconds,
          retry_count: draft.retry_count,
          classification: draft.classification,
          input_schema: {},
          http_config: {
            method: 'POST',
            url: draft.mcp_url,
            external_mcp: buildExternalMcpConfig({
              mcp_url: draft.mcp_url,
              mcp_transport: draft.mcp_transport,
              mcp_auth_type: draft.mcp_auth_type,
              mcp_auth_header_name: draft.mcp_auth_header_name,
              mcp_auth_secret_id: draft.mcp_auth_secret_id,
              discovered_tools: mcpDiscoveredTools,
              enabled_remote_tools: selectedTools.map((tool) => tool.remote_tool_name),
            }),
          },
        }
        const created = await toolsService.createAction(payload)
        if (draft.emoji && created.id) saveToolEmoji(created.id, draft.emoji)
        setCreatedCount(1)
      } else {
        const payload: MCPActionCreate = {
          name: derivedToolName,
          display_name: draft.display_name.trim(),
          description: draft.description.trim(),
          execution_type: toolType!,
          is_enabled: draft.is_enabled,
          timeout_seconds: draft.timeout_seconds,
          retry_count: draft.retry_count,
          classification: draft.classification,
        }
        if (draft.http_config.url) {
          const schemaPayload = buildWebhookSchemasFromStudioDrafts(
            draft.http_config.method,
            draft.http_config.path_params_schema,
            draft.http_config.query_params_schema
          )
          payload.http_config = {
            method: draft.http_config.method,
            url: draft.http_config.url,
            request_headers: toRequestHeadersPayload(draft.http_config.request_headers),
            ...schemaPayload,
          }
        }
        const created = await toolsService.createAction(payload)
        if (draft.emoji && created.id) saveToolEmoji(created.id, draft.emoji)
        setCreatedCount(1)
      }
      setConfettiSeed((prev) => prev + 1)
      clearToolStudioPersistedDraft()
      setStep(totalSteps + 1)
    } catch (error) {
      setErrorMessage(mapStudioError(error))
    } finally {
      setCreating(false)
    }
  }

  /* Header helpers */
  const addHeader = () => {
    updateDraft('http_config', {
      ...draft.http_config,
      request_headers: [...draft.http_config.request_headers, createHeaderDraft()],
    })
  }
  const removeHeader = (id: string) => {
    updateDraft('http_config', {
      ...draft.http_config,
      request_headers: draft.http_config.request_headers.filter((header) => header.id !== id),
    })
  }
  const updateHeader = (id: string, patch: Partial<HeaderDraft>) => {
    updateDraft('http_config', {
      ...draft.http_config,
      request_headers: draft.http_config.request_headers.map((header) =>
        header.id === id ? { ...header, ...patch } : header
      ),
    })
  }

  const addParam = (kind: 'path_params_schema' | 'query_params_schema') => {
    setDraft((prev) => ({
      ...prev,
      http_config: {
        ...prev.http_config,
        [kind]: [...prev.http_config[kind], createParamDraft()],
      },
    }))
  }
  const updateParam = (kind: 'path_params_schema' | 'query_params_schema', paramIndex: number, patch: Partial<ParamDraft>) => {
    setDraft((prev) => {
      const currentParam = prev.http_config[kind][paramIndex]
      const previousId = currentParam?.id?.trim() || ''
      const nextParams = prev.http_config[kind].map((param, index) =>
        index === paramIndex
          ? (() => {
              const nextType = patch.type ?? param.type
              const nextValueType = patch.value_type ?? param.value_type
              const nextValue = nextValueType === 'llm_prompt' ? '' : (patch.value ?? param.value)
              const nextEnum =
                (nextType === 'string' || nextType === 'number' || nextType === 'integer') &&
                nextValueType === 'llm_prompt'
                  ? sanitizeEnumValues(patch.enum ?? param.enum)
                  : []
              return {
                ...param,
                ...patch,
                value: nextValue,
                enum: nextEnum,
                draft_id: param.draft_id || `param_draft_${kind}_${index}`,
              }
            })()
          : param
      )
      if (kind !== 'path_params_schema') {
        return {
          ...prev,
          http_config: {
            ...prev.http_config,
            [kind]: nextParams,
          },
        }
      }
      const nextId = (nextParams[paramIndex]?.id || '').trim()
      let nextUrl = prev.http_config.url
      if (previousId && nextId && previousId !== nextId) {
        nextUrl = renamePathPlaceholderInUrl(nextUrl, previousId, nextId)
      } else if (!previousId && nextId) {
        nextUrl = ensurePathPlaceholderInUrl(nextUrl, nextId)
      } else if (previousId && !nextId) {
        nextUrl = removePathPlaceholderFromUrl(nextUrl, previousId)
      }
      return {
        ...prev,
        http_config: {
          ...prev.http_config,
          url: nextUrl,
          [kind]: nextParams,
        },
      }
    })
  }

  const removeParam = (kind: 'path_params_schema' | 'query_params_schema', paramIndex: number) => {
    const target = draft.http_config[kind][paramIndex]
    if (target) {
      const paramKey = buildParamStableKey(kind, target, paramIndex)
      setParamEnumInputByKey((prev) => {
        if (!(paramKey in prev)) return prev
        const next = { ...prev }
        delete next[paramKey]
        return next
      })
    }
    setDraft((prev) => ({
      ...prev,
      http_config: {
        ...prev.http_config,
        url: kind === 'path_params_schema' ? removePathPlaceholderFromUrl(prev.http_config.url, target?.id || '') : prev.http_config.url,
        [kind]: prev.http_config[kind].filter((_, index) => index !== paramIndex),
      },
    }))
  }

  const setParamEnumInput = (paramKey: string, value: string) => {
    setParamEnumInputByKey((prev) => ({ ...prev, [paramKey]: value }))
  }

  const addParamEnumValue = (kind: 'path_params_schema' | 'query_params_schema', param: ParamDraft, paramIndex: number) => {
    const paramKey = buildParamStableKey(kind, param, paramIndex)
    const draftValue = (paramEnumInputByKey[paramKey] || '').trim()
    if (!draftValue) return
    const nextEnum = sanitizeEnumValues([...(param.enum || []), draftValue])
    updateParam(kind, paramIndex, { enum: nextEnum })
    setParamEnumInput(paramKey, '')
  }

  const renderParamEnumEditor = (
    kind: 'path_params_schema' | 'query_params_schema',
    param: ParamDraft,
    paramIndex: number
  ) => {
    if (
      (param.type !== 'string' && param.type !== 'number' && param.type !== 'integer') ||
      param.value_type !== 'llm_prompt'
    ) {
      return null
    }
    const paramKey = buildParamStableKey(kind, param, paramIndex)
    const enumInputValue = paramEnumInputByKey[paramKey] || ''
    const normalizedCandidate = enumInputValue.trim()
    const canAddEnum = normalizedCandidate.length > 0 && !param.enum.includes(normalizedCandidate)

    return (
      <div className="space-y-2">
        <Label className="text-xs font-medium">Enum Values (optional)</Label>
        <div className="flex items-center gap-2">
          <Input
            value={enumInputValue}
            onChange={(event) => setParamEnumInput(paramKey, event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              if (!canAddEnum) return
              addParamEnumValue(kind, param, paramIndex)
            }}
            placeholder="Introduce un valor enum"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => addParamEnumValue(kind, param, paramIndex)}
            disabled={!canAddEnum}
            aria-label="Añadir valor enum"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Añade valores predefinidos que el LLM pueda seleccionar. Si no se proporcionan valores, el LLM puede usar cualquier valor de texto.
        </p>
        {param.enum.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {param.enum.map((enumValue, enumIndex) => (
              <TagBadge
                key={`${paramKey}-enum-${enumValue}-${enumIndex}`}
                id={`${enumIndex}`}
                name={enumValue}
                variant="secondary"
                size="sm"
                removable
                onRemove={() => updateParam(kind, paramIndex, { enum: param.enum.filter((_, index) => index !== enumIndex) })}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  /* ---------------------------------------------------------------- */
  /*  Step 0: Type Selector                                           */
  /* ---------------------------------------------------------------- */

  if (toolType === null) {
    return createPortal(
      <TypeSelector onSelect={handleTypeSelect} onCancel={closeStudio} />,
      document.body
    )
  }

  /* ---------------------------------------------------------------- */
  /*  Steps 1-4: Main Flow                                            */
  /* ---------------------------------------------------------------- */

  const { label: typeLabel } = TYPE_INFO[toolType]

  const step2Subtitle =
    toolType === 'webhook' ? 'Configura la petición HTTP que se ejecutará.' :
    'Conecta el servidor MCP, explora sus herramientas y selecciona cuáles habilitar.'

  const stepKey = `tool-studio-${editorMode}-${step}`
  const overlay = (
    // No es un modal: es la pantalla entera del estudio, que ocupa la ruta. No
    // hay nada detras a lo que devolver el foco ni scroll de fondo que bloquear.
    // eslint-disable-next-line no-restricted-syntax -- pantalla completa de ruta, no modal
    <div className="fixed inset-0 z-40 flex bg-background">
      {step > totalSteps && <CelebrationConfetti seed={confettiSeed} />}

      {/* Mobile header — shown instead of sidebar on small screens */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-1 border-b bg-background px-2 py-2 h-12">
          <Button variant="ghost" size="icon-xs" className="shrink-0" onClick={closeStudio}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs text-muted-foreground shrink-0">
              {step <= totalSteps ? `${step}/${totalSteps}` : '✓'}
            </span>
            <div className="h-1.5 w-16 rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="flex-1" />
          {step <= totalSteps && (
            <Button variant="outline" size="xs" className="shrink-0" onClick={() => {
              if (editorMode === 'form') { const snapshot = buildJsonSnapshot(); setJsonDraft(snapshot); setJsonError(null); setEditorMode('json'); return; }
              setEditorMode('form');
            }}>
              {editorMode === 'form' ? 'JSON' : 'Form'}
            </Button>
          )}
          <Button variant="ghost" size="icon-xs" className="shrink-0" disabled={step <= 1} onClick={() => goToStep(step - 1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon-xs" className="shrink-0" disabled={step > totalSteps} onClick={() => goToStep(step + 1)}>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Sidebar — desktop only */}
      {!isMobile && (
      <div className="flex w-[290px] shrink-0 flex-col border-r border-border bg-muted/20">
        <div className="p-3 pb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeStudio}
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tools
          </Button>
        </div>
        <div className="px-4 pb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Tool Studio</p>
            <h1 className="mt-1 text-lg font-semibold leading-tight">Create tool</h1>
          </div>
        </div>
        <div className="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Progress {progress}%
        </div>
        <div className="mx-4 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
        <ScrollArea className="mt-3 flex-1">
          <div className="px-3 pb-3">
            {stepTitles.map((title, idx) => {
              const itemStep = idx + 1
              const status = getWizardStepStatus({
                itemStep,
                currentStep: step > totalSteps ? totalSteps : step,
                visitedSteps,
                isStepValid,
              })
              return (
                <WizardSidebarStep
                  key={title}
                  onClick={() => goToStep(itemStep)}
                  status={status}
                  stepLabel={`Step ${itemStep}`}
                  title={title}
                />
              )
            })}
          </div>
        </ScrollArea>
        <div className="border-t border-border p-3">
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Summary</p>
            <p className="mt-1">Type: {typeLabel}</p>
            <p>Status: {draft.is_enabled ? 'Enabled' : 'Disabled'}</p>
            <p>Visibility: {draft.classification}</p>
          </div>
        </div>
      </div>
      )}

      <div className={cn("flex min-w-0 flex-1 flex-col", isMobile && "pt-12")}>
        <div className={cn("flex h-12 items-center justify-between border-b border-border px-4", isMobile && "hidden")}>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-lg">Tool Studio</Badge>
            <span className="text-sm text-muted-foreground">
              {step <= totalSteps ? `Step ${step} of ${totalSteps}` : 'Completed'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {step <= totalSteps && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  if (editorMode === 'form') {
                    const snapshot = buildJsonSnapshot()
                    setJsonDraft(snapshot)
                    setJsonError(null)
                    setEditorMode('json')
                    return
                  }
                  setJsonError(null)
                  setEditorMode('form')
                }}
              >
                {editorMode === 'form' ? 'Editar JSON' : 'Editar formulario'}
              </Button>
            )}
            <Button variant="ghost" size="icon-xs" onClick={closeStudio}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {errorMessage && step <= totalSteps && (
          <div className="px-6 pt-4">
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          </div>
        )}

        <div className="flex-1 min-h-0">
          <AnimatePresence mode="wait">
            {editorMode === 'json' && step <= totalSteps && (
              <motion.div
                key="tool-studio-json"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
                  title="Editar herramienta en JSON"
                  subtitle="Modifica el draft completo en JSON y aplica los cambios."
                  footer={
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setJsonError(null)
                          setEditorMode('form')
                        }}
                      >
                        {isMobile ? '← Form' : 'Volver al formulario (sin aplicar)'}
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const snapshot = buildJsonSnapshot()
                            setJsonDraft(snapshot)
                            setJsonError(null)
                          }}
                        >
                          {isMobile ? 'Reset' : 'Reset desde formulario'}
                        </Button>
                        <Button size="sm" onClick={applyJsonDraft}>
                          {isMobile ? 'Aplicar' : 'Aplicar JSON'}
                        </Button>
                      </div>
                    </div>
                  }
                >
                  <div className="space-y-2">
                    <Label htmlFor="tool-json-editor">Payload JSON</Label>
                    <Textarea
                      id="tool-json-editor"
                      value={jsonDraft}
                      onChange={(e) => {
                        setJsonDraft(e.target.value)
                        if (jsonError) setJsonError(null)
                      }}
                      className="min-h-[460px] font-mono text-xs"
                      placeholder='{"execution_type":"webhook","name":"..."}'
                    />
                    {jsonError && <p className="whitespace-pre-line text-sm text-destructive">{jsonError}</p>}
                  </div>
                </StepLayout>
              </motion.div>
            )}

            {/* ── Step 1: Identity ── */}
            {editorMode === 'form' && step === 1 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
            title="Identidad de la herramienta"
            subtitle="Define cómo se llama y para qué sirve esta herramienta."
            footer={
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={closeStudio}>Cancelar</Button>
                <Button onClick={() => goToStep(2)} disabled={!validateStep1}>
                  Siguiente <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            }
          >
            <div className="space-y-8">
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium">Tipo de herramienta</p>
                  <p className="text-xs text-muted-foreground">Puedes cambiarlo en cualquier momento.</p>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {(['webhook', 'mcp_server'] as ToolType[]).map((type) => {
                    const option = TYPE_INFO[type]
                    const active = toolType === type
                    return (
                      <Button
                        key={type}
                        type="button"
                        variant={active ? 'default' : 'outline'}
                        onClick={() => handleTypeChangeAtStepOne(type)}
                        className="justify-start gap-2"
                      >
                        <option.Logo className="h-5 w-5 shrink-0" />
                        <span className="min-w-0 truncate font-medium">{option.label}</span>
                      </Button>
                    )
                  })}
                </div>
              </div>

              {/* Emoji picker */}
              <div className="flex items-center gap-4">
                <SelectorDeEmoji emoji={draft.emoji} onElegir={(em) => updateDraft('emoji', em)} />
                <div>
                  <p className="text-sm font-medium">Icono de la herramienta</p>
                  <p className="text-xs text-muted-foreground">Elige un emoji para identificar visualmente esta herramienta</p>
                </div>
              </div>

              <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="tool-display-name">Nombre visible *</Label>
                      <Input
                        id="tool-display-name"
                        value={draft.display_name}
                        onChange={(e) => updateDraft('display_name', e.target.value)}
                        placeholder="ej: Buscar Propiedades"
                      />
                      <Label>Nombre interno *</Label>
                      <div className="rounded-lg border bg-muted/30 px-3 py-2 font-mono text-sm text-foreground">
                        {derivedToolName || '-'}
                      </div>
                      {loadingInternalNames && derivedToolName.length > 0 && (
                        <p className="text-xs text-muted-foreground">Validando disponibilidad del nombre interno...</p>
                      )}
                      {!loadingInternalNames && internalNameTaken && (
                        <p className="text-xs text-destructive">{t('agent_studio.internal_name_collision')}</p>
                      )}
                      {internalNamesError && (
                        <p className="text-xs text-warning">{internalNamesError}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {t('agent_studio.internal_name_auto_help')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tool-description">Descripción</Label>
                  <Textarea
                    id="tool-description"
                    value={draft.description}
                    onChange={(e) => updateDraft('description', e.target.value)}
                    placeholder="Qué hace esta herramienta y cuándo se usa..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Visibilidad de herramienta</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { value: 'external', label: 'Público' },
                      { value: 'internal', label: 'Privado' },
                    ] as const).map((option) => {
                      const active = draft.classification === option.value
                      return (
                        <Button
                          key={option.value}
                          type="button"
                          variant={active ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateDraft('classification', option.value)}
                        >
                          {option.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Estado</p>
                    <p className={cn('text-xs font-medium', draft.is_enabled ? 'text-success' : 'text-warning')}>
                      {draft.is_enabled ? 'Habilitado' : 'Deshabilitado'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn(draft.is_enabled ? 'border-success/30 text-success' : 'border-warning/30 text-warning')}>
                      {draft.is_enabled ? 'ON' : 'OFF'}
                    </Badge>
                    <Switch
                      checked={draft.is_enabled}
                      onCheckedChange={(checked) => updateDraft('is_enabled', checked)}
                    />
                  </div>
                </div>
              </div>
            </div>
                </StepLayout>
              </motion.div>
            )}

            {/* ── Step 2: Execution (type-aware) ── */}
            {editorMode === 'form' && step === 2 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
            title="Configuración de ejecución"
            subtitle={step2Subtitle}
            footer={
              <div className="flex items-center justify-between gap-2">
                <Button variant="ghost" size="sm" onClick={() => goToStep(1)}>
                  <ArrowLeft className="mr-1 h-4 w-4" /> Anterior
                </Button>
                <div className="flex items-center gap-2">
                  {!isMobile && (
                    <Button variant="outline" size="sm" onClick={() => setConfirmResetOpen(true)}>
                      Iniciar de nuevo
                    </Button>
                  )}
                  <Button size="sm" onClick={() => goToStep(3)} disabled={!validateStep2}>
                    Siguiente <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            }
          >
            <div className="space-y-8">
              {/* Webhook */}
              {toolType === 'webhook' && (
                <>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_1fr]">
                      <div className="space-y-2">
                        <Label>Método HTTP</Label>
                        <Select
                          value={draft.http_config.method}
                          onValueChange={(v: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') =>
                            updateDraft('http_config', { ...draft.http_config, method: v })
                          }
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GET">GET</SelectItem>
                            <SelectItem value="POST">POST</SelectItem>
                            <SelectItem value="PUT">PUT</SelectItem>
                            <SelectItem value="PATCH">PATCH</SelectItem>
                            <SelectItem value="DELETE">DELETE</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tool-url">URL *</Label>
                        <Input
                          id="tool-url"
                          value={draft.http_config.url}
                          onChange={(e) => syncDraftUrlAndPathParams(e.target.value)}
                          placeholder="https://api.example.com/endpoint"
                          required
                        />
                        {draft.http_config.url.trim().length > 0 && !isValidHttpUrl(draft.http_config.url) && (
                          <p className="text-xs text-destructive">
                            URL inválida. Debe comenzar con `http://` o `https://`.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Headers</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                          <Plus className="mr-1 h-4 w-4" /> Añadir
                        </Button>
                      </div>
                      {draft.http_config.request_headers.map((header) => (
                        <div key={header.id} className="grid grid-cols-1 gap-2 md:grid-cols-[1.1fr_190px_1.2fr_auto]">
                          <Input
                            placeholder="Header"
                            value={header.name}
                            onChange={(e) => updateHeader(header.id, { name: e.target.value })}
                          />
                          <Select
                            value={header.value_type}
                            onValueChange={(value: HeaderDraft['value_type']) => updateHeader(header.id, { value_type: value })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="valor">Valor</SelectItem>
                              <SelectItem value="variable_dinamica">Variable dinamica</SelectItem>
                              <SelectItem value="secreto">Secreto</SelectItem>
                            </SelectContent>
                          </Select>
                          {header.value_type === 'secreto' ? (
                            <SecretSelectField value={header.value} onChange={(secretId) => updateHeader(header.id, { value: secretId })} />
                          ) : (
                            <Input
                              placeholder={header.value_type === 'variable_dinamica' ? 'Nombre de variable' : 'Valor del header'}
                              value={header.value}
                              onChange={(e) => updateHeader(header.id, { value: e.target.value })}
                            />
                          )}
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeHeader(header.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="tool-timeout">Timeout (segundos)</Label>
                        <Input id="tool-timeout" type="number" value={draft.timeout_seconds} onChange={(e) => updateDraft('timeout_seconds', parseInt(e.target.value) || 30)} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tool-retry">Reintentos</Label>
                        <Input id="tool-retry" type="number" value={draft.retry_count} onChange={(e) => updateDraft('retry_count', parseInt(e.target.value) || 0)} />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* MCP Server */}
              {toolType === 'mcp_server' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tool-mcp-url">URL del servidor MCP *</Label>
                    <Input
                      id="tool-mcp-url"
                      value={draft.mcp_url}
                      onChange={(e) => {
                        updateDraft('mcp_url', e.target.value)
                        setMcpDiscoveredTools([])
                        setMcpExploreError(null)
                      }}
                      placeholder="https://mcp.example.com/rpc"
                      required
                    />
                    {draft.mcp_url.trim().length > 0 && !isValidHttpUrl(draft.mcp_url) && (
                      <p className="text-xs text-destructive">
                        URL inválida. Debe comenzar con `http://` o `https://`.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-1">
                      <Label>Auth type</Label>
                      <Select
                        value={draft.mcp_auth_type}
                        onValueChange={(value: 'none' | 'bearer' | 'header') => {
                          if (value === 'none') {
                            setDraft((prev) => ({ ...prev, mcp_auth_type: value, mcp_auth_secret_id: '' }))
                          } else {
                            updateDraft('mcp_auth_type', value)
                          }
                          setMcpDiscoveredTools([])
                          setMcpExploreError(null)
                        }}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">none</SelectItem>
                          <SelectItem value="bearer">bearer</SelectItem>
                          <SelectItem value="header">header</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {draft.mcp_auth_type !== 'none' && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>API key / token</Label>
                        <SecretSelectField
                          value={draft.mcp_auth_secret_id}
                          onChange={(secretId) => {
                            updateDraft('mcp_auth_secret_id', secretId)
                            setMcpDiscoveredTools([])
                            setMcpExploreError(null)
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium">Explorar herramientas del servidor</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={discoverMcpTools}
                        disabled={mcpDiscovering}
                      >
                        {mcpDiscovering ? 'Explorando...' : 'Explorar herramientas'}
                      </Button>
                    </div>
                    {mcpExploreError && (
                      <p className="text-xs text-destructive">{mcpExploreError}</p>
                    )}
                    {mcpDiscoveredTools.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Selecciona qué herramientas quieres habilitar. Seleccionadas: {selectedMcpToolsCount}/{mcpDiscoveredTools.length}
                        </p>
                        <div className="max-h-56 space-y-2 overflow-auto pr-1">
                          {mcpDiscoveredTools.map((tool) => (
                            <div key={tool.remote_tool_name} className="flex items-center justify-between gap-2 rounded-lg border p-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium">{tool.display_name || tool.remote_tool_name}</p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={() => setMcpToolDetail(tool)}>
                                  Ver detalle
                                </Button>
                                <Switch
                                  checked={tool.enabled}
                                  onCheckedChange={(checked) => toggleMcpDiscoveredTool(tool.remote_tool_name, checked)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* <div className="space-y-2">
                    <Label htmlFor="tool-mcp-timeout">Timeout (segundos)</Label>
                    <Input id="tool-mcp-timeout" type="number" value={draft.timeout_seconds} onChange={(e) => updateDraft('timeout_seconds', parseInt(e.target.value) || 30)} />
                  </div> */}
                </div>
              )}

            </div>
                </StepLayout>
              </motion.div>
            )}

            {/* ── Step 3: Parameters (Webhook only) ── */}
            {editorMode === 'form' && step === 3 && toolType !== 'mcp_server' && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
	                <StepLayout isMobile={isMobile}
	            title="Parámetros"
	            subtitle="Define parámetros de ruta y consulta para la herramienta."
	            footer={
	              <div className="flex items-center justify-between">
	                <Button variant="ghost" onClick={() => goToStep(2)}><ArrowLeft className="mr-2 h-4 w-4" /> Anterior</Button>
	                <Button onClick={() => goToStep(4)}>Siguiente <ArrowRight className="ml-2 h-4 w-4" /></Button>
	              </div>
	            }
	          >
	            <div className="space-y-8">
	              <>
	              <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Parámetros de ruta</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => addParam('path_params_schema')}>
                      <Plus className="mr-1 h-4 w-4" /> Añadir parámetro
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Estos parámetros se insertan en la URL como {"{param}"} o ":param". Ejemplo: si defines "id", usa /recurso/{"{id}"}.
                  </p>
                  {draft.http_config.path_params_schema.length > 0 && (
                  <div className="hidden items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground md:grid md:grid-cols-[minmax(220px,1.4fr)_140px_170px_auto]">
                    <span>Clave (ID)</span>
                    <span>Tipo</span>
                    <span>Origen valor</span>
                    <span />
                  </div>
                  )}
                  {draft.http_config.path_params_schema.map((param, paramIndex) => {
                    const rowKey = param.draft_id || `path-param-${paramIndex}`
                    const idInputRefKey = buildParamInputRefKey('path_params_schema', param, paramIndex, 'id')
                    const descriptionInputRefKey = buildParamInputRefKey('path_params_schema', param, paramIndex, 'description')
                    const valueInputRefKey = buildParamInputRefKey('path_params_schema', param, paramIndex, 'value')
                    return (
                    <div key={rowKey} className="space-y-2 rounded-lg border p-3">
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(220px,1.4fr)_140px_170px_auto]">
                        <Input
                          ref={(node) => setParamInputNode(idInputRefKey, node)}
                          value={param.id}
                          onChange={(e) => {
                            rememberParamInputFocus(idInputRefKey, e.currentTarget)
                            updateParam('path_params_schema', paramIndex, { id: e.target.value })
                          }}
                          placeholder="item_id"
                          aria-label="Clave del parámetro de ruta"
                        />
                        <Select value={param.type} onValueChange={(value: ParamDraft['type']) => updateParam('path_params_schema', paramIndex, { type: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="string">String</SelectItem>
                            <SelectItem value="boolean">Boolean</SelectItem>
                            <SelectItem value="integer">Integer</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={param.value_type} onValueChange={(value: ParamDraft['value_type']) => updateParam('path_params_schema', paramIndex, { value_type: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="llm_prompt">LLM Prompt</SelectItem>
                            <SelectItem value="constant">Valor constante</SelectItem>
                            <SelectItem value="dynamic_variable">Variable dinamica</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeParam('path_params_schema', paramIndex)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_220px]">
                        <Input
                          ref={(node) => setParamInputNode(descriptionInputRefKey, node)}
                          value={param.description}
                          onChange={(e) => {
                            rememberParamInputFocus(descriptionInputRefKey, e.currentTarget)
                            updateParam('path_params_schema', paramIndex, { description: e.target.value })
                          }}
                          placeholder="Descripción para extracción"
                        />
                        {param.value_type !== 'llm_prompt' && (
                          <Input
                            ref={(node) => setParamInputNode(valueInputRefKey, node)}
                            value={param.value}
                            onChange={(e) => {
                              rememberParamInputFocus(valueInputRefKey, e.currentTarget)
                              updateParam('path_params_schema', paramIndex, { value: e.target.value })
                            }}
                            placeholder={param.value_type === 'constant' ? 'Valor constante' : 'Nombre variable'}
                          />
                        )}
                      </div>
                      {renderParamEnumEditor('path_params_schema', param, paramIndex)}
                      <div className="inline-flex items-center gap-2 text-xs">
                        <Switch
                          checked={param.required}
                          onCheckedChange={(checked) => updateParam('path_params_schema', paramIndex, { required: checked })}
                        />
                        <span className={cn('font-medium', param.required ? 'text-success' : 'text-muted-foreground')}>
                          Requerido
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            'h-5 rounded-md px-1.5 text-xs uppercase tracking-wide',
                            param.required ? 'border-success/30 text-success' : 'border-border text-muted-foreground'
                          )}
                        >
                          {param.required ? 'Si' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  )})}
                </div>
	              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Parámetros de consulta</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addParam('query_params_schema')}>
                    <Plus className="mr-1 h-4 w-4" /> Añadir parámetro
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  GET y DELETE envían estos parámetros en query string. POST, PUT y PATCH los envían en el body.
                </p>
                {draft.http_config.query_params_schema.length > 0 && (
                <div className="hidden items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground md:grid md:grid-cols-[minmax(220px,1.4fr)_140px_170px_auto]">
                  <span>Clave (query key)</span>
                  <span>Tipo</span>
                  <span>Origen valor</span>
                  <span />
                </div>
                )}
                {draft.http_config.query_params_schema.map((param, paramIndex) => {
                  const rowKey = param.draft_id || `query-param-${paramIndex}`
                  const idInputRefKey = buildParamInputRefKey('query_params_schema', param, paramIndex, 'id')
                  const descriptionInputRefKey = buildParamInputRefKey('query_params_schema', param, paramIndex, 'description')
                  const valueInputRefKey = buildParamInputRefKey('query_params_schema', param, paramIndex, 'value')
                  return (
                  <div key={rowKey} className="space-y-2 rounded-lg border p-3">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(220px,1.4fr)_140px_170px_auto]">
                      <Input
                        ref={(node) => setParamInputNode(idInputRefKey, node)}
                        value={param.id}
                        onChange={(e) => {
                          rememberParamInputFocus(idInputRefKey, e.currentTarget)
                          updateParam('query_params_schema', paramIndex, { id: e.target.value })
                        }}
                        placeholder="city"
                        aria-label="Clave del parámetro de consulta"
                      />
                      <Select value={param.type} onValueChange={(value: ParamDraft['type']) => updateParam('query_params_schema', paramIndex, { type: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="integer">Integer</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={param.value_type} onValueChange={(value: ParamDraft['value_type']) => updateParam('query_params_schema', paramIndex, { value_type: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="llm_prompt">LLM Prompt</SelectItem>
                          <SelectItem value="constant">Valor constante</SelectItem>
                          <SelectItem value="dynamic_variable">Variable dinamica</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeParam('query_params_schema', paramIndex)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_220px]">
                      <Input
                        ref={(node) => setParamInputNode(descriptionInputRefKey, node)}
                        value={param.description}
                        onChange={(e) => {
                          rememberParamInputFocus(descriptionInputRefKey, e.currentTarget)
                          updateParam('query_params_schema', paramIndex, { description: e.target.value })
                        }}
                        placeholder="Descripción para extracción"
                      />
                      {param.value_type !== 'llm_prompt' && (
                        <Input
                          ref={(node) => setParamInputNode(valueInputRefKey, node)}
                          value={param.value}
                          onChange={(e) => {
                            rememberParamInputFocus(valueInputRefKey, e.currentTarget)
                            updateParam('query_params_schema', paramIndex, { value: e.target.value })
                          }}
                          placeholder={param.value_type === 'constant' ? 'Valor constante' : 'Nombre variable'}
                        />
                      )}
                    </div>
                    {renderParamEnumEditor('query_params_schema', param, paramIndex)}
                    <div className="inline-flex items-center gap-2 text-xs">
                      <Switch
                        checked={param.required}
                        onCheckedChange={(checked) => updateParam('query_params_schema', paramIndex, { required: checked })}
                      />
                      <span className={cn('font-medium', param.required ? 'text-success' : 'text-muted-foreground')}>
                        Requerido
                      </span>
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-5 rounded-md px-1.5 text-xs uppercase tracking-wide',
                          param.required ? 'border-success/30 text-success' : 'border-border text-muted-foreground'
                        )}
                      >
                        {param.required ? 'Si' : 'No'}
                      </Badge>
                    </div>
                  </div>
                )})}
	              </div>
	              </>
	            </div>
	                </StepLayout>
	              </motion.div>
	            )}

            {/* ── Review ── */}
            {editorMode === 'form' && step === reviewStep && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
            title="Revisión"
            subtitle="Confirma los datos antes de crear la herramienta."
            footer={
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => goToStep(reviewStep - 1)}><ArrowLeft className="mr-2 h-4 w-4" /> Anterior</Button>
                <Button onClick={createTool} disabled={creating || !isStepValid(reviewStep)}>
                  {creating ? 'Creando...' : 'Crear herramienta'}
                  {!creating && <Check className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            }
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Nombre visible</p>
                {editingField === 'display_name' ? (
                  <Input
                    autoFocus
                    value={draft.display_name}
                    onBlur={() => setEditingField(null)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === 'Escape') setEditingField(null)
                    }}
                    onChange={(e) => updateDraft('display_name', e.target.value)}
                    className="mt-2"
                  />
                ) : (
                  <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('display_name')}>
                    {draft.display_name || '-'}
                  </Button>
                )}
                <Separator className="my-4" />

                <p className="text-xs uppercase tracking-wide text-muted-foreground">Nombre interno</p>
                <p className="mt-1 font-mono text-sm text-primary">
                  {derivedToolName || '-'}
                </p>
                <Separator className="my-4" />

                <p className="text-xs uppercase tracking-wide text-muted-foreground">Descripción</p>
                {editingField === 'description' ? (
                  <Textarea
                    autoFocus
                    value={draft.description}
                    onBlur={() => setEditingField(null)}
                    onChange={(e) => updateDraft('description', e.target.value)}
                    rows={3}
                    className="mt-2"
                  />
                ) : (
                  <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('description')}>
                    {draft.description || 'Sin descripción'}
                  </Button>
                )}
                <Separator className="my-4" />

                <p className="text-xs uppercase tracking-wide text-muted-foreground">Estado</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{typeLabel}</Badge>
                  <Badge variant="outline" className="capitalize">{draft.classification}</Badge>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className={cn(
                        'rounded-full',
                        draft.is_enabled ? 'bg-success/20 text-success hover:bg-success/30' : 'bg-muted text-muted-foreground',
                      )}
                      onClick={() => updateDraft('is_enabled', !draft.is_enabled)}
                    >
                      {draft.is_enabled ? 'Habilitado' : 'Deshabilitado'}
                    </Button>
                    <Switch
                      checked={draft.is_enabled}
                      onCheckedChange={(checked) => updateDraft('is_enabled', checked)}
                    />
                  </div>
                </div>
              </div>

              <div>
                {toolType === 'webhook' && (
                  <>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Timeout</p>
                    {editingField === 'timeout_seconds' ? (
                      <Input
                        autoFocus
                        type="number"
                        value={draft.timeout_seconds}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === 'Escape') setEditingField(null)
                        }}
                        onChange={(e) => updateDraft('timeout_seconds', parseInt(e.target.value, 10) || 30)}
                        className="mt-2"
                      />
                    ) : (
                      <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('timeout_seconds')}>
                        {draft.timeout_seconds}
                      </Button>
                    )}
                    <Separator className="my-4" />

                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Reintentos</p>
                    {editingField === 'retry_count' ? (
                      <Input
                        autoFocus
                        type="number"
                        value={draft.retry_count}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === 'Escape') setEditingField(null)
                        }}
                        onChange={(e) => updateDraft('retry_count', parseInt(e.target.value, 10) || 0)}
                        className="mt-2"
                      />
                    ) : (
                      <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('retry_count')}>
                        {draft.retry_count}
                      </Button>
                    )}
                    <Separator className="my-4" />
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Método</p>
                    {editingField === 'http_method' ? (
                      <Select
                        value={draft.http_config.method}
                        onValueChange={(v: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') =>
                          updateDraft('http_config', { ...draft.http_config, method: v })
                        }
                      >
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GET">GET</SelectItem>
                          <SelectItem value="POST">POST</SelectItem>
                          <SelectItem value="PUT">PUT</SelectItem>
                          <SelectItem value="PATCH">PATCH</SelectItem>
                          <SelectItem value="DELETE">DELETE</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('http_method')}>
                        {draft.http_config.method}
                      </Button>
                    )}
                    <Separator className="my-4" />

                    <p className="text-xs uppercase tracking-wide text-muted-foreground">URL</p>
                    {editingField === 'http_url' ? (
                      <Input
                        autoFocus
                        value={draft.http_config.url}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === 'Escape') setEditingField(null)
                        }}
                        onChange={(e) => syncDraftUrlAndPathParams(e.target.value)}
                        className="mt-2"
                      />
                    ) : (
                      <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('http_url')}>
                        {draft.http_config.url || 'Sin URL'}
                      </Button>
                    )}
                    <Separator className="my-4" />

                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Headers</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {draft.http_config.request_headers.length > 0
                        ? draft.http_config.request_headers
                            .filter((header) => header.name.trim())
                            .map((header) => {
                              const base = `${header.name} (${header.value_type})`
                              if (header.value_type === 'secreto' && !header.value.trim()) return `${base} · sin secreto`
                              if (header.value_type === 'variable_dinamica') return `${base} · no soportado`
                              return base
                            })
                            .join(', ')
                        : 'Sin headers'}
                    </p>

                    <Separator className="my-4" />
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Parámetros de ruta</p>
                    {draft.http_config.path_params_schema.length === 0 ? (
                      <p className="mt-1 text-sm text-muted-foreground">Sin parámetros de ruta</p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {draft.http_config.path_params_schema.map((param, paramIndex) => (
                          <div key={`review-path-${param.id}-${paramIndex}`} className="rounded-lg border p-2">
                            <Badge variant="outline" className="text-xs">
                              {param.id} · {param.type} · {param.value_type}
                              {param.required ? ' · req' : ''}
                              {(param.type === 'string' || param.type === 'number' || param.type === 'integer') &&
                              param.value_type === 'llm_prompt' &&
                              param.enum.length > 0
                                ? ` · enum:${param.enum.length}`
                                : ''}
                            </Badge>
                            <Input
                              className="mt-2 h-8 text-xs"
                              value={param.description}
                              onChange={(e) => updateParam('path_params_schema', paramIndex, { description: e.target.value })}
                              placeholder="Descripción para extracción"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <Separator className="my-4" />
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Parámetros de consulta</p>
                    {draft.http_config.query_params_schema.length === 0 ? (
                      <p className="mt-1 text-sm text-muted-foreground">Sin parámetros de consulta</p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {draft.http_config.query_params_schema.map((param, paramIndex) => (
                          <div key={`review-query-${param.id}-${paramIndex}`} className="rounded-lg border p-2">
                            <Badge variant="outline" className="text-xs">
                              {param.id} · {param.type} · {param.value_type}
                              {param.required ? ' · req' : ''}
                              {(param.type === 'string' || param.type === 'number' || param.type === 'integer') &&
                              param.value_type === 'llm_prompt' &&
                              param.enum.length > 0
                                ? ` · enum:${param.enum.length}`
                                : ''}
                            </Badge>
                            <Input
                              className="mt-2 h-8 text-xs"
                              value={param.description}
                              onChange={(e) => updateParam('query_params_schema', paramIndex, { description: e.target.value })}
                              placeholder="Descripción para extracción"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {toolType === 'mcp_server' && (
                  <>
                    <Separator className="my-4" />
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">URL MCP</p>
                    {editingField === 'mcp_url' ? (
                      <Input
                        autoFocus
                        value={draft.mcp_url}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === 'Escape') setEditingField(null)
                        }}
                        onChange={(e) => updateDraft('mcp_url', e.target.value)}
                        className="mt-2"
                      />
                    ) : (
                      <Button variant="link" className="mt-1 justify-start text-left \1" onClick={() => setEditingField('mcp_url')}>
                        {draft.mcp_url || 'Sin URL'}
                      </Button>
                    )}
                    <Separator className="my-4" />
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Herramientas seleccionadas</p>
                    <p className="mt-1 text-sm text-muted-foreground">{selectedMcpToolsCount}</p>
                  </>
                )}

              </div>
            </div>
              </StepLayout>
              </motion.div>
            )}

            {/* ── Success ── */}
            {step > totalSteps && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full min-h-0"
              >
                <ScrollArea className="h-full">
                  <div className="mx-auto flex min-h-full w-full max-w-2xl items-start justify-center px-6 py-8">
                    <div className="w-full py-8 text-center">
                      <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
                        <CheckCircle2 className="h-8 w-8 text-success" />
                      </div>
                      <h2 className="mt-4 text-2xl font-semibold tracking-tight">Herramienta creada</h2>
                      <p className="mt-2 text-muted-foreground">
                        {createdCount > 1
                          ? <span className="font-medium text-foreground">{createdCount} herramientas</span>
                          : <span className="font-medium text-foreground">{draft.display_name}</span>}
                        {' '}listas para usarse.
                      </p>
                      <div className="mt-6 grid gap-2 md:grid-cols-2">
                        <Button variant="outline" onClick={() => setToolType(null)}>
                          <Plus className="mr-2 h-4 w-4" /> Crear otra
                        </Button>
                        <Button onClick={closeStudio}>Ir a Herramientas</Button>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <AlertDialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Iniciar de nuevo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se perderán todos los datos cargados en el Tool Studio actual. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={resetStudioFromScratch}>Sí, iniciar de nuevo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Salir de Tool Studio?</AlertDialogTitle>
            <AlertDialogDescription>
              Puedes guardar el borrador para continuar luego o descartarlo ahora.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={discardDraftAndClose}>
              Descartar y salir
            </Button>
            <AlertDialogAction onClick={keepDraftAndClose}>
              Guardar borrador y salir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={Boolean(mcpToolDetail)} onOpenChange={(open) => { if (!open) setMcpToolDetail(null) }}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>{mcpToolDetail?.display_name || mcpToolDetail?.remote_tool_name || 'Detalle de herramienta'}</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {mcpToolDetail?.remote_tool_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 overflow-auto pr-1">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Descripción</p>
              <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">
                {mcpToolDetail?.description || 'Sin descripción'}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Input schema</p>
              <pre className="mt-1 max-h-[40vh] overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
                {JSON.stringify(mcpToolDetail?.input_schema || {}, null, 2)}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )

  return createPortal(overlay, document.body)
}
